import SNSWebSDK from '@sumsub/websdk';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { KYC_POPUP_INITIATE, POPUP_TYPE, KYC_TYPES_STATUS } from '@constants';
import { userAction, popupAction, kycSumsubAction } from '@redux/action';

export const useGetKYCSumsubData = () => {
    const dispatch = useDispatch();
    const portal = useSelector((state) => state.portal);
    const user = useSelector((state) => state.user);
    const isKYCSumsubEnabled = portal?.settings?.features?.kycSumsub?.isEnabled || false;

    useEffect(() => {
        if (!user?.isLogin || !isKYCSumsubEnabled) return;

        const fetchKYCData = async () => {
            try {
                const data = await window.SPL_Member.getMemberKycStatus();
                const applicants = data?.applicants ?? [];
                if (applicants.length === 0) return;

                const activeLevel = applicants.find((item) => item?.isActiveLevel === true);
                const initByCompliance = data?.source === KYC_POPUP_INITIATE.COMPLIANCE;
                const displayNotification = data?.displayNotification || false;

                if (activeLevel && initByCompliance && displayNotification) {
                    dispatch(popupAction.setPopup(POPUP_TYPE.KYC_SUMSUB, true));

                    try {
                        window.SPL_Member.updateSumsubNotification();
                    } catch (err) {
                        console.warn('Failed to update Sumsub notification:', err);
                    }
                }

                dispatch(userAction.getMemberKYCSumsubInfo(data));
            } catch (error) {
                console.error('Error fetching member KYC status:', error);
            }
        };

        fetchKYCData();
    }, [dispatch, isKYCSumsubEnabled, user.isLogin]);
};

export const useSumsub = (step, index) => {
    const dispatch = useDispatch();
    const { i18n } = useTranslation();
    const language = i18n.language;

    const user = useSelector((state) => state.user);

    const getAccessToken = async () => {
        try {
            const params = { externalUserId: user?.account?.login, levelName: step || 'L1' };
            const response = await generateKYCSumsubToken(params);
            const token = response?.token;

            return token;
        } catch (error) {
            console.log('error', error);
        }
    };

    const generateKYCSumsubToken = (params) => {
        return window.SPL_Member.generateKYCSumsubToken(params)
            .then((res) => {
                return res;
            })
            .catch((error) => {
                return error;
            });
    };

    const launchKYC = async (token) => {
        try {
            const snsWebSdkInstance = SNSWebSDK.init(
                token,
                // Add this updateAccessToken callback
                async () => {
                    const newToken = await getAccessToken();
                    return newToken;
                }
            )
                .withConf({
                    lang: language, //language of WebSDK texts and comments (ISO 639-1 format)
                    // email: applicantEmail,
                    // phone: applicantPhone,
                    theme: 'light', //light or dark theme
                })
                .on('idCheck.onStepCompleted', (payload) => {
                    onKycSubmitResolved(step, index, payload);
                    console.log('onStepCompleted', payload);
                })
                .on('idCheck.onError', (error) => {
                    onKycSubmitRejected(step, index, error);
                    console.log('onError', error);
                })
                .build();

            snsWebSdkInstance.launch('#sumsub-websdk-container');
        } catch (error) {
            console.error('Failed to launch KYC:', error);
            dispatch(kycSumsubAction.setError(error));
        }
    };

    const onKycSubmitPending = async (data, index) => {
        await generateKYCSumsubTokenPending(data, index);
    };

    const onKycSubmitRejected = async (data, index) => {
        await generateKYCSumsubTokenRejected(data, index);
    };

    const onKycSubmitResolved = async (data, index) => {
        await generateKYCSumsubTokenResolved(data, index);
    };

    const generateKYCSumsubTokenPending = async (data, index) => {
        try {
            // Simulate API call or actual logic for updating step
            const updatedStepData = await updateStepPendingStatus(data);

            dispatch(kycSumsubAction.updateStep({ index, updatedStepData }));
        } catch (error) {
            console.error('Error updating step:', error);
            // Handle error (e.g., show error message to user)
        }
    };

    const updateStepPendingStatus = (data) => {
        return {
            ...data,
            status: KYC_TYPES_STATUS.PENDING,
            statusText: 'kyc:pending',
            statusIcon: '/public/html/images/common_img/icon-pending.svg',
        };
    };

    const generateKYCSumsubTokenResolved = async (data, index) => {
        try {
            // Simulate API call or actual logic for updating step
            const updatedStepData = await updateStepSuccessStatus(data);

            dispatch(kycSumsubAction.updateStep({ index, updatedStepData }));
        } catch (error) {
            console.error('Error updating step:', error);
            // Handle error (e.g., show error message to user)
        }
    };

    const updateStepSuccessStatus = (data) => {
        return {
            ...data,
            status: KYC_TYPES_STATUS.APPROVED,
            statusText: 'kyc:completed',
            statusIcon: '/public/html/images/common_img/icon-resolved.svg',
        };
    };

    const generateKYCSumsubTokenRejected = async (data, index) => {
        try {
            // Simulate API call or actual logic for updating step
            const updatedStepData = await updateStepRejectedStatus(data);
            dispatch(kycSumsubAction.updateStep({ index, updatedStepData }));
        } catch (error) {
            console.error('Error updating step:', error);
            // Handle error (e.g., show error message to user)
        }
    };

    const updateStepRejectedStatus = (data) => {
        return {
            ...data,
            status: KYC_TYPES_STATUS.REJECTED,
            statusText: 'kyc:rejected',
            statusIcon: '/public/html/images/common_img/icon-error.svg',
        };
    };

    const fetchMemberKYCSumsubInfo = () => {
        return window.SPL_Member.getMemberKycStatus()
            .then((res) => {
                dispatch(userAction.getMemberKYCSumsubInfo(res));
            })
            .catch((error) => {
                console.error(error);
            });
    };

    return { launchKYC, getAccessToken, onKycSubmitPending, onKycSubmitRejected, onKycSubmitResolved, fetchMemberKYCSumsubInfo };
};
